import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../../environments/environment';
import {ChatMessage} from '../models/chat-message';
import {ChatDataService} from 'projects/chat-common/src/lib/services/chat.data.service';
import * as moment from 'moment';


interface ChatCompletionResponse {
  id: string;
  choices: Array<{
    index: number;
    message: { role: string; content: string };
    finish_reason: string;
  }>;
}

@Injectable({providedIn: 'root'})
export class OpenAIService {
  private url = environment.openaiUrl;
  private http: HttpClient = inject(HttpClient);

  constructor(private userDataService: ChatDataService) {
  }

  async requestHotTopics(): Promise<string[]> {
    let message = await this.userDataService.getUserDataMessage();
    let parsedMessage = new ChatMessage(message.message);
    let hotTopicsMessage = new ChatMessage("Please generate me hot topics (7 maximum) which user might be interested in based on this user data which you have in history.")
    return await this.sendGPTCustomMessage([parsedMessage, hotTopicsMessage]);
  }

  async requestFertilityHotTopics(): Promise<string[]> {
    let message = await this.userDataService.getUserDataMessage();
    let parsedMessage = new ChatMessage(message.message);
    let hotTopicsMessage = new ChatMessage("Please generate me hot topics (7 maximum) which user might be interested in based on this user data which you have in history. Hot topics must be about fertility in general.")
    return await this.sendGPTCustomMessage([parsedMessage, hotTopicsMessage]);
  }

  async requestHealthyPregnancyTopics(): Promise<string[]> {
    let message = await this.userDataService.getUserDataMessage();
    let parsedMessage = new ChatMessage(message.message);
    let hotTopicsMessage = new ChatMessage("Please generate me hot topics (7 maximum) which user might be interested in based on this user data which you have in history. Hot topics must be about healthy pregnancy in general.")
    return await this.sendGPTCustomMessage([parsedMessage, hotTopicsMessage]);
  }

  async requestPregnancyWeekHotTopics(): Promise<string[]> {
    let message = await this.userDataService.getUserDataMessage();
    let parsedMessage = new ChatMessage(message.message);
    let hotTopicsMessage = new ChatMessage("Please generate me hot topics (7 maximum) which user might be interested in based on this user data which you have in history. Hot topics must be about current week of user pregnancy.")
    return await this.sendGPTCustomMessage([parsedMessage, hotTopicsMessage]);
  }

  async requestDateOfBirth(date: string): Promise<string> {
    const dateOfBirthMessage = new ChatMessage(
      `Please find me date of birth in format 'YYYY-MM-DD' from this string "${date}".
     If given only age/year, send January 1st of that year (calculate age).
      Send just string in correct format  (that date in correct format).`
    );
    let answer = await this.sendGPTOnboardingMessage(dateOfBirthMessage);
    return answer;
  }

  async requestDateOfPeriod(date: string): Promise<string> {
    const dateOfBirthMessage = new ChatMessage(
      `Please find me due date or last period date in format 'YYYY-MM-DD' from this string "${date}. Today is ${moment().format('YYYY-MM-DD')} ".
     Send just string in correct format (that date in correct format).`
    );
    let answer = await this.sendGPTOnboardingMessage(dateOfBirthMessage);
    return answer;

  }


  async sendGPTIntroMessages(messages: ChatMessage[]): Promise<any[]> {
    let messagesMapped = messages.map((m) => ({
      role: m.source,
      content: m.message
    }));

    let systemMessage = {
      role: 'system',
      content: `
You are a health assistant chat system. Based on user data received, you must generate exactly 4 messages as valid JSON objects, following these strict rules:

Introductory Message Rules (First 4) — Authoritative

Message 1:
- Purpose: Informational about current status (cycle day/phase; if pregnant → week/trimester; expected next ovulation/period). Try to use exact dates of period/ovulation arrival. Use it in human format OR YYYY.DD.MM. or for eg. Sep 29th.
- CTA: "INTRO"
- Card: "C1"
- Options: []

Message 2:
- Purpose: Expanded context (phase hormones; what’s typical to track now).
- CTA: "INTRO"
- Card: "C1"
- Options: []

Message 3:
- Purpose: Show today’s key info + logged data (supportive sentence + structured info).
- CTA: "INTRO" (⚠️ must be INTRO, not SINGLE/MULTI/TEXT/NONE).
- Card: "C3"
- Options: choose any relevant subset of:
  { "action": "showInfo#steps", "display": "Steps: {todaySteps}" }
  { "action": "showInfo#sleep", "display": "Sleep: {todaySleepHours}h" }
  { "action": "showInfo#activity", "display": "Activity: {todayActivity}" }
  { "action": "showInfo#hydration", "display": "Hydration: {todayHydration}L" }
  { "action": "showInfo#loggedMoods", "display": "Logged moods: {moodsToday}" }
  { "action": "showInfo#loggedSymptoms", "display": "Logged symptoms: {symptomsToday}" }
  { "action": "showInfo#cyclePrediction", "display": "Next cycle prediction in {days} days, {date}" }
  { "action": "showInfo#ovulationPrediction", "display": "Next ovulation prediction in {days} days, {date}" }
  { "action": "showInfo#commonSymptoms", "display": "Common symptoms in this phase: {symptomList}" }
  { "action": "showInfo#hormoneLevels", "display": "Hormone levels: {hormoneSummary}" }
   If you detect non-ovulary cycle, do not show ovulation prediction.

Message 4 (sentinel):
Must be exactly this JSON (no variation):

{
  "message": "Ask Health Assistant. I’m here to help, let me know what I can help you with or start typing in the text field. C4 SENT",
  "answer": { "cta": "NONE", "card": "C4", "options": [] }
}

General JSON schema for each message:

{
  "message": "Your conversational message here...",
  "answer": {
    "cta": "SINGLE" | "MULTI" | "TEXT" | "NONE" | "INTRO",
    "card": "C1" | "C2" | "C3" | "C5" | "C4",
    "options": [
      {
        "action": "someAction",
        "display": "Human readable text"
      }
    ]
  }
}

⚠️ Important: Always return a JSON array with exactly 4 objects. No markdown, no extra text.
`
    };

    const body = {
      messages: [systemMessage, ...messagesMapped],
      model: 'gpt-4.1-2025-04-14',
      temperature: 0.7,
      max_tokens: 1024,
    };

    try {
      const res = await firstValueFrom(
        this.http.post<ChatCompletionResponse>(this.url, body)
      );

      let raw = res?.choices?.[0]?.message?.content ?? '';
      let parsed = JSON.parse(raw);

      if (!Array.isArray(parsed) || parsed.length !== 4) {
        throw new Error('Invalid GPT response format, expected 4 messages');
      }

      return parsed;
    } catch (err: any) {
      const msg =
        err?.error?.error?.message ||
        err?.error?.message ||
        err?.message ||
        'OpenAI request failed';
      throw new Error(msg);
    }
  }


  async sendGPTCustomMessage(messages: ChatMessage[]): Promise<string[]> {
    let messagesMapped = messages.map((m) => ({'role': m.source, 'content': m.message}));
    let systemMessage = {
      'role': 'system',
      'content': 'In return message content, just write hot topics titles, divided by # (and do not use # in topic title): For example: Reproductive health#Pregnancy#Postpartum'
    };
    const body = {
      messages: [systemMessage, ...messagesMapped],
      model: 'gpt-4.1-2025-04-14',
      temperature: 1.0,
      top_p: 1,
      max_tokens: 2048,
    };

    try {
      const res = await firstValueFrom(
        this.http.post<ChatCompletionResponse>(this.url, body)
      );
      let topics = res?.choices?.[0]?.message?.content ?? ''
      if (topics && topics.length > 0) {
        return topics.split("#").map((t) => t.trim());
      }
      return [];
    } catch (err: any) {
      const msg =
        err?.error?.error?.message ||
        err?.error?.message ||
        err?.message ||
        'OpenAI request failed';
      throw new Error(msg);
    }
  }

  async sendGPTOnboardingMessage(systemMessage: ChatMessage): Promise<string> {
    let messagesMapped = [systemMessage].map((m) => ({'role': 'system', 'content': m.message}));
    const body = {
      messages: [...messagesMapped],
      model: 'gpt-4.1-2025-04-14',
      temperature: 1.0,
      top_p: 1,
      max_tokens: 2048,
    };

    try {
      const res = await firstValueFrom(
        this.http.post<ChatCompletionResponse>(this.url, body)
      );
      let answer = res?.choices?.[0]?.message?.content ?? ''
      return answer
    } catch (err: any) {
      const msg =
        err?.error?.error?.message ||
        err?.error?.message ||
        err?.message ||
        'OpenAI request failed';
      throw new Error(msg);
    }
  }
}
